// React Dependencies
import React, { useEffect, useMemo, useState } from 'react';

// External Dependencies
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
} from '@material-ui/core';

import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';

import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import arrayMove from 'array-move';

import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

// Internal Dependencies
import apiService from '../../services.js/apiService';
import { IsArray, IsEmpty } from '../../common/checks';
import useDebounce from '../global/use-debounce';
import MText from '../common/MText';
import MButton from '../common/MButton';
import MTextField from '../common/MTextField';

import PhotoCard from './photoCard';
import SortablePinnedPhoto from './sortablePinnedPhoto';
import PhotoPanel from './photoPanel';
import EmptyState from './emptyState';
import Colours from '../../styles/colours';
import Loading from '../global/Loading';
import { PinnedPhotoLocation } from '../../common/constants';

// ============================================================
// PILL INPUT STYLES
// ============================================================

const useFilterStyles = makeStyles(() => ({
  pillRoot: {
    borderRadius: 18,
  },
  pillOutline: {
    borderRadius: 18,
  },
  pillInput: {
    fontSize: 13,
  },
  groupWrapper: {
    display:         'flex',
    alignItems:      'stretch',
    border:          '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius:    18,
    overflow:        'hidden',
    backgroundColor: '#fff',
  },
  groupOutline: {
    border: 'none',
  },
  groupDivider: {
    width:           1,
    backgroundColor: 'rgba(0, 0, 0, 0.23)',
  },
  mPillOutline: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '18px !important',
    },
  },
  mGroupOutline: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  mCompactField: {
    '& .MuiOutlinedInput-root': {
      padding:   '0px !important',
      minHeight: '30px !important',
      height:    '30px !important',
    },
    '& .MuiOutlinedInput-input': {
      padding:   '5px 10px !important',
      fontSize:  '12px !important',
      height:    '20px !important',
      boxSizing: 'border-box !important',
    },
    '& .MuiSelect-select.MuiSelect-selectMenu': {
      display:    'flex !important',
      alignItems: 'center !important',
      padding:    '0px 10px !important',
      fontSize:   '12px !important',
      height:     '30px !important',
      minHeight:  '30px !important',
      boxSizing:  'border-box !important',
    },
  },
}));

// ============================================================
// CONSTANTS
// ============================================================

// Requested resolution for image fetching (display size is
// responsive via the grid, this is just the source quality).
const PHOTO_SIZE = 300;
const FILTER_SECTION_HEIGHT = 112;
const PHOTO_CONTAINER_HEIGHT = '50vh';

// ============================================================
// MAIN COMPONENT
// ============================================================

const SnapstyrFavs = () => {
  const classes = useFilterStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [allPhotos, setAllPhotos] = useState([]);
  const [pinnedPhotoIds, setPinnedPhotoIds] = useState([]);
  const [pinnedPhotoDetails, setPinnedPhotoDetails] = useState({});
  const [filters, setFilters] = useState({
    poiName:    '',
    tag:        '',
    shopper:    'Shopper',
    nameOrPage: '',
  });
  const [loading, setLoading] = useState(false);
  const [isOverLoading, setIsOverloading] = useState(false);
  const debouncedFilters = useDebounce(filters, 500);
  const [photoSortOrder, setPhotoSortOrder] = useState('newest');
  const [photoPendingRemoval, setPhotoPendingRemoval] = useState(null);

  const fetchAllPhotos = async () => {
    try {
      setLoading(true);

      const params = {
        poiName: debouncedFilters.poiName,
        tag:     debouncedFilters.tag,
        ...(filters.shopper === 'Shopper' ? { shopperName: debouncedFilters.nameOrPage } : { pageName: debouncedFilters.nameOrPage }),
      };

      const result = await apiService.getPhotosForPinning(params);

      const rows = IsArray(result.rows) ? result.rows : [];

      const normalizedRows = rows.map((photo) => ({
        ...photo,
        id: Number(photo.id),
      }));

      setAllPhotos(normalizedRows);
    } catch (error) {
      setAllPhotos([]);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // LOAD EXISTING PINNED PHOTOS
  // ==========================================================
  //
  // Always sorted ascending by seq (the last saved display
  // order) — sort-by-newest/oldest on Pinned/Fav Photos has
  // been removed, so there's only one loaded order, matching
  // whatever was last saved via handleSave.
  //

  const loadPinnedPhotos = async () => {
    try {
      const result = await apiService.getPinnedPhotos({ location: PinnedPhotoLocation.SnapstyrFavs });

      const rows = IsArray(result.rows) ? result.rows : [];

      const sortedRows = [...rows].sort(
        (a, b) => a.seq - b.seq,
      );

      const sortedIds = sortedRows.map((row) => Number(row.photoId));

      const details = {};

      sortedRows.forEach((row) => {
        const photoId = Number(row.photoId);

        details[photoId] = {
          id:       photoId,
          photoUrl: row.photoUrl,
        };
      });

      setPinnedPhotoDetails(details);
      setPinnedPhotoIds(sortedIds);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchAllPhotos();
  }, [
    debouncedFilters.poiName,
    debouncedFilters.tag,
    debouncedFilters.nameOrPage,
  ]);

  useEffect(() => {
    if (IsEmpty(debouncedFilters.nameOrPage)) {
      return;
    }

    fetchAllPhotos();
  }, [filters.shopper]);

  useEffect(() => {
    loadPinnedPhotos();
  }, []);

  const pinnedPhotos = useMemo(() => (
    pinnedPhotoIds
      .map((photoId) => pinnedPhotoDetails[photoId])
      .filter(Boolean)
  ), [pinnedPhotoIds, pinnedPhotoDetails]);

  const snapstyrPhotos = useMemo(() => {
    const pinnedSet = new Set(pinnedPhotoIds);

    return allPhotos.filter(
      (photo) => !pinnedSet.has(photo.id),
    );
  }, [allPhotos, pinnedPhotoIds]);

  const sortedSnapstyrPhotos = useMemo(() => {
    const getTime = (photo) => new Date(photo.createdAt).getTime();

    return [...snapstyrPhotos].sort(
      (a, b) => (
        photoSortOrder === 'newest'
          ? getTime(b) - getTime(a)
          : getTime(a) - getTime(b)
      ),
    );
  }, [snapstyrPhotos, photoSortOrder]);

  const handleFilterChange = (key, value) => {
    setFilters(
      (previous) => ({
        ...previous,
        [key]: value,
      }),
    );
  };

  // ==========================================================
  // SELECT / MOVE PHOTO TO PINNED
  // ==========================================================
  //
  // Newly pinned photos always go to the front (position 1) —
  // there is no sort-by mode on Pinned/Fav Photos anymore to
  // vary this insert position.
  //

  const handleSelectPhoto = (photo) => {
    const photoId = Number(photo.id);

    setPinnedPhotoIds((previous) => {
      if (previous.includes(photoId)) {
        return previous;
      }

      return [photoId, ...previous];
    });

    setPinnedPhotoDetails((previous) => ({
      ...previous,
      [photoId]: photo,
    }));
  };

  const handleRequestRemovePinnedPhoto = (photo) => {
    setPhotoPendingRemoval(photo);
  };

  const handleConfirmRemovePinnedPhoto = () => {
    if (!photoPendingRemoval) {
      return;
    }

    const idToRemove = photoPendingRemoval.id;

    setPinnedPhotoIds(
      (previous) => previous.filter(
        (photoId) => photoId !== idToRemove,
      ),
    );

    setPhotoPendingRemoval(null);
  };

  const handleCancelRemovePinnedPhoto = () => {
    setPhotoPendingRemoval(null);
  };

  const handlePinnedDragEnd = ({ active, over }) => {
    if (!over) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    setPinnedPhotoIds((previous) => {
      const oldIndex = previous.indexOf(active.id);
      const newIndex = previous.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return previous;
      }

      return arrayMove(previous, oldIndex, newIndex);
    });
  };

  // ==========================================================
  // SAVE
  // ==========================================================
  //
  // pinnedPhotoIds' array order IS the display order (from
  // pin-click order and/or drag reordering) — seq always just
  // mirrors the array index.
  //

  const handleSave = async () => {
    setIsOverloading(true);

    const payload = pinnedPhotoIds.map(
      (photoId, index) => ({
        photoId,
        seq: index + 1,
      }),
    );

    try {
      await apiService.updatePinnedPhotos({ photos: payload, location: PinnedPhotoLocation.SnapstyrFavs });

      toast.success('Snapstyr Favs photos saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsOverloading(false);
    }
  };

  return (
    <>
      {isOverLoading && <Loading />}
      <Box
        width="100%"
        minHeight="100%"
        bgcolor={Colours.white}
        px={3}
      >
        {/* ====================================================
            HEADER
        ===================================================== */}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <MText
              variant="h6"
              style={{
                fontWeight: 600,
                color:      '#222',
                fontSize:   18,
              }}
            >
              Manage Snapstyr Favs
            </MText>
          </Box>
        </Box>

        {/* ====================================================
            TWO COLUMN LAYOUT
            alignItems="stretch" makes both Grid items (and the
            PhotoPanels inside them) share the same height, equal
            to whichever panel is naturally taller.
        ===================================================== */}

        <Grid
          container
          spacing={2}
          alignItems="stretch"
        >

          {/* ==================================================
              SNAPSTYR PHOTOS
          =================================================== */}

          <Grid
            item
            xs={12}
            md={6}
            style={{ display: 'flex' }}
          >
            <PhotoPanel
              title="Snapstyr Photos"
              subtitle={`${snapstyrPhotos.length} available`}
            >

              {/* ==============================================
                  FILTERS
              =============================================== */}
              <Box
                px={1.5}
                py={1.25}
                height={isMobile ? 'auto' : FILTER_SECTION_HEIGHT}
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                flexWrap={isMobile ? 'nowrap' : 'nowrap'}
                alignItems={isMobile ? 'stretch' : 'center'}
                style={{ gap: 12 }}
              >

                {/* LEFT: 2x2 filter grid */}
                <Box flexGrow={1} minWidth={0}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <MTextField
                        className={`${classes.mPillOutline} ${classes.mCompactField}`}
                        placeholder="Poi name"
                        value={filters.poiName}
                        multiLine={false}
                        fullWidth
                        style={{ width: '100%' }}
                        onChange={(value) => handleFilterChange('poiName', value)}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <MTextField
                        className={`${classes.mPillOutline} ${classes.mCompactField}`}
                        placeholder="Tag"
                        value={filters.tag}
                        multiLine={false}
                        fullWidth
                        style={{ width: '100%' }}
                        onChange={(value) => handleFilterChange('tag', value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box className={classes.groupWrapper}>

                        <Box flex={1} display="flex" alignItems="center">
                          <MTextField
                            select
                            className={classes.mCompactField}
                            value={filters.shopper}
                            hideBorder
                            fullWidth
                            style={{ width: '100%' }}
                            onChange={(value) => handleFilterChange('shopper', value)}
                          >
                            <MenuItem value="Shopper">Shopper</MenuItem>
                            <MenuItem value="Photographer">Photographer</MenuItem>
                          </MTextField>
                        </Box>

                        <Box className={classes.groupDivider} />

                        <Box flex={1} display="flex" alignItems="center">
                          <MTextField
                            className={classes.mCompactField}
                            placeholder="Name or page"
                            value={filters.nameOrPage}
                            multiLine={false}
                            hideBorder
                            fullWidth
                            style={{ width: '100%' }}
                            onChange={(value) => handleFilterChange('nameOrPage', value)}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* RIGHT: Sort By — beside filters on desktop, full-width below on mobile */}
                <Box
                  style={{
                    flexShrink: 0,
                    width:      isMobile ? '100%' : 'auto',
                  }}
                >
                  <MTextField
                    select
                    className={`${classes.mPillOutline} ${classes.mCompactField}`}
                    value={photoSortOrder}
                    fullWidth={isMobile}
                    style={{ minWidth: isMobile ? 'auto' : 150 }}
                    onChange={(value) => setPhotoSortOrder(value)}
                  >
                    <MenuItem value="newest">Sort By : Newest</MenuItem>
                    <MenuItem value="oldest">Sort By : Oldest</MenuItem>
                  </MTextField>
                </Box>
              </Box>

              {/* ==============================================
                  PHOTO CONTAINER (flex-grow, fills panel height)
              =============================================== */}

              <Box
                px={1.5}
                height={PHOTO_CONTAINER_HEIGHT}
                overflow="auto"
              >
                {loading ? (
                  <Box
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <MText
                      variant="body2"
                      color="textSecondary"
                    >
                      Loading photos...
                    </MText>
                  </Box>
                ) : IsEmpty(snapstyrPhotos) ? (
                  <EmptyState
                    title="No available photos"
                    description={
                      !IsEmpty(pinnedPhotos)
                        ? 'All photos are currently pinned'
                        : 'No photos found'
                    }
                  />
                ) : (
                  <Box
                    display="grid"
                    style={{
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap:                 8,
                    }}
                    pb={1.5}
                  >
                    {sortedSnapstyrPhotos.map(
                      (photo) => (
                        <PhotoCard
                          key={photo.id}
                          photo={photo}
                          photoSize={PHOTO_SIZE}
                          onSelect={handleSelectPhoto}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>

              {/* ==============================================
                  FOOTER
              =============================================== */}

              <Box
                px={1.5}
                py={1.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                borderTop={`1px solid ${Colours.LightGray}`}
              >

                <MText
                  variant="caption"
                  style={{
                    color:      '#1976d2',
                    fontWeight: 500,
                  }}
                >
                  {pinnedPhotos.length}
                  {' '}
                  pinned
                </MText>
              </Box>
            </PhotoPanel>
          </Grid>

          {/* ==================================================
              PINNED / FAV PHOTOS
          =================================================== */}

          <Grid
            item
            xs={12}
            md={6}
            style={{ display: 'flex' }}
          >
            <PhotoPanel
              title="Fav Photos"
              subtitle={
                !IsEmpty(pinnedPhotos)
                  ? `${pinnedPhotos.length} photos · Drag to reorder`
                  : 'No pinned photos'
              }
              headerAction={(
                <MButton
                  variant="contained"
                  text="Save Changes"
                  disabled={loading || isOverLoading}
                  onClick={handleSave}
                  textSize={13}
                  textColour={Colours.White}
                  colour="#1976d2"
                  disableElevation
                  style={{
                    minWidth: 110,
                    height:   32,
                  }}
                />
              )}
            >

              {/* ==============================================
                  HEADER — sort-by control removed. Kept as an
                  empty spacer at the same FILTER_SECTION_HEIGHT
                  as Snapstyr Photos' filter row, so both panels'
                  photo grids and footers stay vertically aligned.
              =============================================== */}

              <Box
                px={1.5}
                py={1.25}
                height={FILTER_SECTION_HEIGHT}
              />

              {/* ==============================================
                  PHOTO CONTAINER (flex-grow, fills panel height)
              =============================================== */}

              <Box
                px={1.5}
                height={PHOTO_CONTAINER_HEIGHT}
                overflow="auto"
              >
                {IsEmpty(pinnedPhotos) ? (
                  <EmptyState
                    title="No fav photos"
                    description="Click a photo on the left to add it as a fav"
                  />
                ) : (
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handlePinnedDragEnd}
                  >
                    <SortableContext
                      items={pinnedPhotoIds}
                      strategy={rectSortingStrategy}
                    >
                      <Box
                        display="grid"
                        style={{
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap:                 8,
                        }}
                        pb={1.5}
                      >
                        {pinnedPhotos.map(
                          (
                            photo,
                            index,
                          ) => (
                            <SortablePinnedPhoto
                              key={
                                photo.id
                              }
                              photo={photo}
                              order={
                                index + 1
                              }
                              photoSize={PHOTO_SIZE}
                              onRemove={
                                handleRequestRemovePinnedPhoto
                              }
                            />
                          ),
                        )}
                      </Box>
                    </SortableContext>
                  </DndContext>
                )}
              </Box>
            </PhotoPanel>
          </Grid>
        </Grid>

        {/* ====================================================
            REMOVE CONFIRMATION MODAL
        ===================================================== */}

        <Dialog
          open={Boolean(photoPendingRemoval)}
          onClose={handleCancelRemovePinnedPhoto}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Remove fav photo?
          </DialogTitle>

          <DialogContent>
            <DialogContentText>
              This photo will be removed from Snapstyr Favs.
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <MButton
              variant="text"
              text="Cancel"
              onClick={handleCancelRemovePinnedPhoto}
            />

            <MButton
              className="ml-4"
              variant="contained"
              text="Remove"
              textColour={Colours.White}
              colour="#d32f2f"
              disableElevation
              onClick={handleConfirmRemovePinnedPhoto}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

SnapstyrFavs.propTypes = {
};

export default SnapstyrFavs;
